import { createContext, useContext, useRef, useState, ReactNode } from 'react'

import { reportError } from '@/utils/reportError'

export interface FormHandler {
  submit: () => Promise<void>
  reset: () => void
  isDirty: () => boolean
}

interface FormGroupContextValue {
  register: (handler: FormHandler) => void
  unregister: (handler: FormHandler) => void
  submitAll: () => Promise<void>
  resetAll: () => void
  isEditing: boolean
  startEditing: () => void
  cancelEditing: () => void
}

const FormGroupContext = createContext<FormGroupContextValue | null>(null)

export const useFormGroupHandler = (): FormGroupContextValue => {
  const ctx = useContext(FormGroupContext)
  if (!ctx) {
    throw new Error(
      'useFormGroupHandler must be used within a FormGroupProvider',
    )
  }
  return ctx
}

export const FormGroupProvider = ({ children }: { children: ReactNode }) => {
  const handlers = useRef<Set<FormHandler>>(new Set())
  const [isEditing, setIsEditing] = useState(false)

  const register = (handler: FormHandler) => handlers.current.add(handler)
  const unregister = (handler: FormHandler) => handlers.current.delete(handler)

  const submitAll = async () => {
    await Promise.all(
      [...handlers.current].map(async (handler) => {
        try {
          await handler.submit()
        } catch (error) {
          reportError(error)
        }
      }),
    )
    setIsEditing(false)
  }

  const resetAll = () => {
    handlers.current.forEach((h) => h.reset())
  }

  const startEditing = () => setIsEditing(true)
  const cancelEditing = () => {
    resetAll()
    setIsEditing(false)
  }

  return (
    <FormGroupContext.Provider
      value={{
        register,
        unregister,
        submitAll,
        resetAll,
        isEditing,
        startEditing,
        cancelEditing,
      }}
    >
      {children}
    </FormGroupContext.Provider>
  )
}
